import React, { useContext, useEffect, useState, useRef } from "react";
import DatePickerWithIcon from "../../../components/elements/DatePickerWithIcon";
import { StorageContext } from "../../../services/StorageProvider";
import SelectInput from "../../ListOfTripRequest/views/SelectInput";
import "../css/HistoricTripsFilter.css";
import { TRIP_STATUS_FOR_FILTER, ERROR_MESSAGE } from "../../../utils/constants";
import { renderAirportCode } from "../../../utils/Utils";
import SearchAirportDialog from "../../../components/elements/SearchAirportDialog";
import SnackbarAlert from "../../../components/elements/SnackbarAlert";
import dayjs from "dayjs";
import { HISTORIC_TRIPS_FILTERS_KEY } from "../../../utils/searchStorage";

const HistoricTripsFilter = ({
  onFilterApplied,
  onFilterCleared,
  resetFilters,
}) => {
  const { airportData } = useContext(StorageContext);
  const [filters, setFilters] = useState({
    selectedArrivalAirport: "",
    selectedDepartureAirport: "",
    selectedDateFrom: null,
    selectedStatus: "",
    registrationNumber: "",
  });
  const [savedFilters, setSavedFilters] = useState(null);
  const hasLoadedSavedFiltersRef = useRef(false);

  useEffect(() => {
    if (resetFilters) {
      setFilters({
        selectedArrivalAirport: "",
        selectedDepartureAirport: "",
        selectedDateFrom: null,
        selectedStatus: "",
        registrationNumber: "",
      });
      setSelectedArrivalAirport(null);
      setSelectedDepartureAirport(null);
      setErrorMessage("");
      localStorage.removeItem(HISTORIC_TRIPS_FILTERS_KEY);
    }
  }, [resetFilters]);

  useEffect(() => {
    if (hasLoadedSavedFiltersRef.current) {
      return;
    }
    hasLoadedSavedFiltersRef.current = true;
    const stored = localStorage.getItem(HISTORIC_TRIPS_FILTERS_KEY);
    if (stored) {
      try {
        setSavedFilters(JSON.parse(stored));
      } catch (error) {
        localStorage.removeItem(HISTORIC_TRIPS_FILTERS_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (!savedFilters) {
      return;
    }

    const restoredFilters = {
      ...savedFilters,
      selectedDateFrom: savedFilters.selectedDateFrom
        ? dayjs(savedFilters.selectedDateFrom)
        : null,
      registrationNumber:
        savedFilters.registrationNumber?.toUpperCase() || "",
    };

    setFilters(restoredFilters);
    const arrivalAirport = (airportData || []).find(
      (airport) => airport.id === restoredFilters.selectedArrivalAirport
    );
    const departureAirport = (airportData || []).find(
      (airport) => airport.id === restoredFilters.selectedDepartureAirport
    );
    setSelectedArrivalAirport(arrivalAirport || null);
    setSelectedDepartureAirport(departureAirport || null);
    setErrorMessage("");
    if (onFilterApplied) {
      onFilterApplied(restoredFilters);
    }
    setSavedFilters(null);
  }, [savedFilters, airportData, onFilterApplied]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Clear error message when date filter changes
    if (key === "selectedDateFrom") {
      setErrorMessage("");
    }
  };

  const [selectedArrivalAirport, setSelectedArrivalAirport] = useState(null);
  const [selectedDepartureAirport, setSelectedDepartureAirport] = useState(null);
  const [showSearchArrivalAirportDialog, setShowSearchArrivalAirportDialog] = useState(false);
  const [showSearchDepartureAirportDialog, setShowSearchDepartureAirportDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarKey, setSnackbarKey] = useState(0);

  const handleArrivalAirportChange = (event) => {
    const selectedId = event.target.value;
    const selectedArrivalAirport = airportData.find(
      (airport) => airport.id === selectedId
    );
    handleFilterChange("selectedArrivalAirport", selectedId);
    setSelectedArrivalAirport(selectedArrivalAirport);
  };

  const handleDepartureAirportChange = (event) => {
    const selectedId = event.target.value;
    const selectedDepartureAirport = airportData.find(
      (airport) => airport.id === selectedId
    );
    handleFilterChange("selectedDepartureAirport", selectedId);
    setSelectedDepartureAirport(selectedDepartureAirport);
  };

  const handleSearchArrivalAirportChange = (airport) => {
    handleFilterChange("selectedArrivalAirport", airport.id);
    setSelectedArrivalAirport(airport);
  };

  const handleSearchDepartureAirportChange = (airport) => {
    handleFilterChange("selectedDepartureAirport", airport.id);
    setSelectedDepartureAirport(airport);
  };

  const handleClearAll = () => {
    setFilters({
      selectedArrivalAirport: "",
      selectedDepartureAirport: "",
      selectedDateFrom: null,
      selectedStatus: "",
      registrationNumber: "",
    });
    setSelectedArrivalAirport(null);
    setSelectedDepartureAirport(null);
    setErrorMessage("");
    localStorage.removeItem(HISTORIC_TRIPS_FILTERS_KEY);
    if (onFilterCleared) {
      onFilterCleared();
    }
  };

  const handleApply = () => {
    // Validate if date is in the future (current date + 1 or later)
    if (filters.selectedDateFrom) {
      const selectedDate = dayjs(filters.selectedDateFrom).startOf("day");
      const today = dayjs().startOf("day");

      // If selected date is tomorrow or later (current date + 1 or more)
      if (selectedDate.isAfter(today)) {
        setErrorMessage(ERROR_MESSAGE.FUTURE_DATE_FILTER_NOT_ALLOWED);
        setSnackbarKey((prev) => prev + 1); // Force remount of snackbar
        return;
      }
    }

    // Clear any previous error message
    setErrorMessage("");

    if (onFilterApplied) {
      onFilterApplied(filters);
      const storedFilters = {
        ...filters,
        selectedDateFrom: filters.selectedDateFrom
          ? filters.selectedDateFrom.toISOString()
          : null,
      };
      localStorage.setItem(
        HISTORIC_TRIPS_FILTERS_KEY,
        JSON.stringify(storedFilters)
      );
    }
  };

  // Check if any filter is selected
  const isAnyFilterSelected = () => {
    return (
      filters.selectedArrivalAirport ||
      filters.selectedDepartureAirport ||
      filters.selectedDateFrom ||
      filters.selectedStatus ||
      (filters.registrationNumber &&
        filters.registrationNumber.trim() !== "")
    );
  };

  const handleAircraftRegistrationNumberChange = (e) => {
    const value = e.target.value.toUpperCase();
    // When user manually clears ARN, reset list like Clear All
    if (!value || value.trim() === "") {
      handleClearAll();
      return;
    }
    handleFilterChange("registrationNumber", value);
  };

  const showButtonsRow = isAnyFilterSelected();

  return (
    <div className="historic-trips-filter-bar">
      <div className="historic-trips-filter-field">
        <div className="historic-trips-filter-label">Select Arrival Airport</div>
        <div
          className="historic-trips-filter-select-airport"
          onClick={() => setShowSearchArrivalAirportDialog(true)}
        >
          {selectedArrivalAirport ? (
            <p>{selectedArrivalAirport.name} - {renderAirportCode(selectedArrivalAirport.iataCode, selectedArrivalAirport.icaoCode)}</p>
          ) : (
            <span className="placeholder-text">Search</span>
          )}
        </div>
      </div>

      <div className="historic-trips-filter-field">
        <div className="historic-trips-filter-label">Select Departure Airport</div>
        <div
          className="historic-trips-filter-select-airport"
          onClick={() => setShowSearchDepartureAirportDialog(true)}
        >
          {selectedDepartureAirport ? (
            <p>{selectedDepartureAirport.name} - {renderAirportCode(selectedDepartureAirport.iataCode, selectedDepartureAirport.icaoCode)}</p>
          ) : (
            <span className="placeholder-text">Search</span>
          )}
        </div>
      </div>

      <DatePickerWithIcon
        label="Departure Date"
        value={filters?.selectedDateFrom}
        onChange={(newValue) =>
          handleFilterChange("selectedDateFrom", newValue)
        }
      />

      <SelectInput
        label="Status"
        value={filters?.selectedStatus}
        onChange={(e) => handleFilterChange("selectedStatus", e.target.value)}
        options={TRIP_STATUS_FOR_FILTER}
      />

      <div className="historic-trips-filter-field">
        <div className="historic-trips-filter-label">ARN</div>
        <input
          type="text"
          className="historic-trips-filter-email-input"
          placeholder="Enter ARN"
          value={filters.registrationNumber || ""}
          onChange={handleAircraftRegistrationNumberChange}
        />
      </div>

      {showButtonsRow && (
        <div className="historic-trips-filter-buttons">
          <div
            className="historic-trips-clear-all-button"
            onClick={handleClearAll}
          >
            Clear All
          </div>
          <button className="historic-trips-apply-button" onClick={handleApply}>
            Apply
          </button>
        </div>
      )}
      <SearchAirportDialog
        open={showSearchArrivalAirportDialog}
        onClose={() => setShowSearchArrivalAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchArrivalAirportChange}
      />
      <SearchAirportDialog
        open={showSearchDepartureAirportDialog}
        onClose={() => setShowSearchDepartureAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchDepartureAirportChange}
      />
      {errorMessage && (
        <SnackbarAlert key={snackbarKey} type="error">{errorMessage}</SnackbarAlert>
      )}
    </div>
  );
};

export default HistoricTripsFilter;
